import { LogEntry } from '@/hooks/useLogStream';
import { unifiedConsole } from './unifiedConsole';

export interface LogFilters {
  level: string;
  categories: string;
  showTiers: boolean;
}

/**
 * Controller for managing log streaming and console injection.
 * Routes backend logs through the unified console system.
 */
export class LogController {

  /**
   * Update log filters in unified console.
   */
  setFilters(filters: Partial<LogFilters>): void {
    unifiedConsole.setFilters({
      level: filters.level || unifiedConsole.getFilters().level,
      categories: filters.categories || unifiedConsole.getFilters().categories,
      streamingEnabled: filters.showTiers !== undefined ? filters.showTiers : unifiedConsole.getFilters().streamingEnabled
    });
  }

  /**
   * Get current filters from unified console.
   */
  getFilters(): LogFilters {
    const unified = unifiedConsole.getFilters();
    return {
      level: unified.level,
      categories: unified.categories,
      showTiers: unified.streamingEnabled
    };
  }


  /**
   * Inject a backend log entry into the browser console via unified console.
   * The unified console will handle all filtering and formatting.
   */
  injectLog(log: LogEntry): void {
    // Extract category (use tier as fallback)
    const category = log.category || log.tier;

    // Format message with username if present
    let message = log.message;
    if (log.username) {
      message = `[user: ${log.username}] ${message}`;
    }

    // Extract metadata
    const metadata = log.metadata || {};
    const cleanMetadata = Object.keys(metadata).reduce((acc: any, key) => {
      if (metadata[key] !== undefined && metadata[key] !== null && metadata[key] !== '') {
        acc[key] = metadata[key];
      }
      return acc;
    }, {});

    // Route through unified console (single point of output)
    // Use the actual tier from the log (API, Database, Storage, etc.)
    if (Object.keys(cleanMetadata).length > 0) {
      unifiedConsole.output(log.tier, category, log.level, message, cleanMetadata);
    } else {
      unifiedConsole.output(log.tier, category, log.level, message);
    }
  }

  /**
   * Save configuration to sessionStorage.
   */
  saveConfig(config: { level: string; categories: string; streamingEnabled: boolean }): void {
    sessionStorage.setItem('logging-config', JSON.stringify(config));
  }

  /**
   * Load configuration from sessionStorage.
   */
  loadConfig(): { level: string; categories: string; streamingEnabled: boolean } | null {
    const stored = sessionStorage.getItem('logging-config');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        // Use unifiedConsole directly to avoid circular dependency
        unifiedConsole.output('FE', 'LogController', 'error', 'Failed to parse stored config:', e);
      }
    }
    return null;
  }
}

// Singleton instance
export const logController = new LogController();